'use client'

import { useState, useEffect } from 'react'
import type { CookieConsentConfig } from '@/../types/sale-page'

interface Props {
  config: CookieConsentConfig
  onAccept: () => void
  onDecline: () => void
}

const COOKIE_KEY = 'fc-cookie-consent'

export function getCookieConsent(): 'accepted' | 'declined' | null {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(COOKIE_KEY) as 'accepted' | 'declined' | null
  } catch {
    return null // Private browsing or localStorage unavailable
  }
}

export default function CookieConsent({ config, onAccept, onDecline }: Props) {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!config.enabled) return
    const stored = getCookieConsent()
    if (!stored) {
      // Delay to avoid layout shift
      const timer = setTimeout(() => setShow(true), 1000)
      return () => clearTimeout(timer)
    }
    // Already consented → fire callback
    if (stored === 'accepted') onAccept()
  }, [config.enabled, onAccept])

  if (!show) return null

  const handleAccept = () => {
    try { localStorage.setItem(COOKIE_KEY, 'accepted') } catch { /* private browsing */ }
    setShow(false)
    onAccept()
  }

  const handleDecline = () => {
    try { localStorage.setItem(COOKIE_KEY, 'declined') } catch { /* private browsing */ }
    setShow(false)
    onDecline()
  }

  const positionClass = {
    'bottom': 'inset-x-0 bottom-0',
    'bottom-left': 'bottom-4 left-4 max-w-md',
    'bottom-right': 'bottom-4 right-4 max-w-md',
  }[config.position] || 'inset-x-0 bottom-0'

  const isBar = config.position === 'bottom'

  return (
    <div className={`fixed ${positionClass} z-[70] animate-slide-up`}>
      <div
        className={`${isBar ? 'mx-0' : 'mx-4'} bg-white border border-gray-200 shadow-2xl ${isBar ? 'rounded-t-2xl' : 'rounded-2xl'} overflow-hidden`}
        style={{ backgroundColor: config.backgroundColor || '#FFFFFF' }}
      >
        <div className={`p-4 ${isBar ? 'sm:flex sm:items-center sm:justify-between sm:gap-4 max-w-6xl mx-auto' : ''}`}>
          <div className="flex-1 mb-3 sm:mb-0">
            <p className="text-sm" style={{ color: config.textColor || '#374151' }}>
              {config.message || 'เว็บไซต์นี้ใช้คุกกี้เพื่อปรับปรุงประสบการณ์การใช้งาน'}
            </p>
            {config.privacyPolicyUrl && (
              <a
                href={config.privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline mt-1 inline-block"
              >
                นโยบายความเป็นส่วนตัว
              </a>
            )}
          </div>
          <div className={`flex gap-2 ${isBar ? 'flex-shrink-0' : ''}`}>
            {config.showDecline && (
              <button
                onClick={handleDecline}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                style={{ color: config.textColor || '#374151' }}
              >
                {config.declineText || 'ปฏิเสธ'}
              </button>
            )}
            <button
              onClick={handleAccept}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition"
              style={{ backgroundColor: config.buttonColor || '#3B82F6' }}
            >
              {config.acceptText || 'ยอมรับทั้งหมด'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
